import { useMemo } from 'react';
import {
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import styles from './OrderOverviewTabView.module.scss';

/**
 * Props:
 *   order  the order detail view model
 */
export const OrderOverviewTabView = ({ order }) => {
  const t = useTranslations();

  const workingSectionFields = useMemo(
    () => [
      { label: t('FIELD_CONTRACTED_QUANTITY'), value: order.byUnit.working?.contractedQuantity },
      { label: t('FIELD_SHIPPED_QUANTITY'), value: order.byUnit.working?.shippedQuantity },
      {
        label: t('ORDERS_COL_REMAINING_TO_SHIP'),
        value: order.byUnit.working?.remainingToShipQuantity,
      },
      { label: t('FIELD_ALLOCATED_QUANTITY'), value: order.byUnit.working?.allocatedQuantity },
      {
        label: t('ORDERS_COL_REMAINING_TO_ALLOCATE'),
        value: order.byUnit.working?.remainingToAllocateQuantity,
      },
      { label: t('ORDERS_COL_TOLERANCE'), value: order.tolerance },
    ],
    [t, order.byUnit.working, order.tolerance],
  );

  const declarationSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_CONTRACTED_QUANTITY'),
        value: order.byUnit.declaration?.contractedQuantity,
      },
      { label: t('FIELD_SHIPPED_QUANTITY'), value: order.byUnit.declaration?.shippedQuantity },
      {
        label: t('ORDERS_COL_REMAINING_TO_SHIP'),
        value: order.byUnit.declaration?.remainingToShipQuantity,
      },
      {
        label: t('FIELD_ALLOCATED_QUANTITY'),
        value: order.byUnit.declaration?.allocatedQuantity,
      },
      {
        label: t('ORDERS_COL_REMAINING_TO_ALLOCATE'),
        value: order.byUnit.declaration?.remainingToAllocateQuantity,
      },
    ],
    [t, order.byUnit.declaration],
  );

  const preferenceFields = useMemo(
    () => [
      { label: t('ORDERS_COL_MAX_GHG_EMISSIONS'), value: order.maxGhgEmissions },
      { label: t('ORDERS_COL_MIN_GHG_SAVINGS'), value: order.minGhgSavings },
      { label: t('FIELD_RAW_MATERIAL_TYPE'), value: order.rawMaterialType },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: order.countryOfOrigin },
      { label: t('FIELD_FEEDSTOCK_CATEGORY'), value: order.feedstockCategory },
      { label: t('FIELD_ALLOCATION_PREFERENCE'), value: order.allocationPreference },
    ],
    [t, order],
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.primary}
        >
          {t('ORDERS_DETAIL_ORDER_DETAILS')}
        </Typography>
      </div>
      <div className={styles.cardBody}>
        {order.workingUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', { unit: order.workingUnit })}
            fields={workingSectionFields}
          />
        )}
        {order.declarationUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', { unit: order.declarationUnit })}
            fields={declarationSectionFields}
          />
        )}
        <FieldSection heading={t('SECTION_PREFERENCES')} fields={preferenceFields} />
      </div>
    </div>
  );
};
